import {
    CACHE_MAX_ENTRIES,
    CACHE_MAX_TEXT_LENGTH,
    CACHE_STORAGE_KEY,
} from '../constants/translationConfig';

/**
 * Remembers translations in the browser (language + original text -> translation).
 * A repeat visit, a reload, or a screen shown again costs no API calls, which keeps us well
 * inside the backend's requests-per-minute limit.
 *
 * Shape in localStorage: { [languageCode]: { [originalText]: translatedText } }
 * Oldest entries are dropped first once CACHE_MAX_ENTRIES is exceeded.
 */
const PERSIST_DELAY_MS = 500;

let data = null;
let persistTimer = null;

const getStorage = () => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const load = () => {
    if (data) return data;
    data = {};
    const storage = getStorage();
    if (!storage) return data;
    try {
        const parsed = JSON.parse(storage.getItem(CACHE_STORAGE_KEY) || 'null');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) data = parsed;
    } catch {
        data = {};
    }
    return data;
};

const countEntries = (cache) =>
    Object.values(cache).reduce((total, byText) => total + Object.keys(byText).length, 0);

/** Drops the oldest entries (from the biggest language first) until the cache fits. */
const trim = (cache) => {
    let total = countEntries(cache);
    while (total > CACHE_MAX_ENTRIES) {
        const biggest = Object.keys(cache).sort(
            (a, b) => Object.keys(cache[b]).length - Object.keys(cache[a]).length
        )[0];
        const oldestKey = Object.keys(cache[biggest])[0];
        delete cache[biggest][oldestKey];
        total -= 1;
    }
};

const persistNow = () => {
    persistTimer = null;
    const storage = getStorage();
    if (!storage || !data) return;
    try {
        storage.setItem(CACHE_STORAGE_KEY, JSON.stringify(data));
    } catch {
        // Storage full or blocked: forget the saved copy rather than break the page.
        try { storage.removeItem(CACHE_STORAGE_KEY); } catch { /* ignore */ }
    }
};

const schedulePersist = () => {
    if (persistTimer) return;
    persistTimer = setTimeout(persistNow, PERSIST_DELAY_MS);
};

export const translationCache = {
    /** @returns {string|undefined} the saved translation, or undefined if not cached */
    get(languageCode, text) {
        return load()[languageCode]?.[text];
    },

    /** @param {Array<[string, string]>} pairs  [originalText, translation] */
    setMany(languageCode, pairs) {
        if (!pairs.length) return;
        const cache = load();
        if (!cache[languageCode]) cache[languageCode] = {};
        let changed = false;
        pairs.forEach(([text, translation]) => {
            if (text.length > CACHE_MAX_TEXT_LENGTH) return;
            cache[languageCode][text] = translation;
            changed = true;
        });
        if (!changed) return;
        trim(cache);
        schedulePersist();
    },

    clear() {
        data = {};
        if (persistTimer) {
            clearTimeout(persistTimer);
            persistTimer = null;
        }
        const storage = getStorage();
        try { storage?.removeItem(CACHE_STORAGE_KEY); } catch { /* ignore */ }
    },

    /** Writes pending changes immediately (used when the page is being closed). */
    flush() {
        if (persistTimer) {
            clearTimeout(persistTimer);
            persistNow();
        }
    },
};

if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => translationCache.flush());
}
